import {
  addressAPI,
  categoriesAPI,
  householdsAPI,
  personAPI,
  routesAPI,
  visitReportsAPI,
} from "../backend/api/api";
import { actions as categoriesActions } from "../backend/store/reducers/categoriesReducer";
import { householdsActions, householdsTriggerActions } from "../backend/store/reducers/householdsReducer";
import { offlineActions } from "../backend/store/reducers/offlineReducer";
import { OfflineDispatch } from "./counters";
import { CONTACTS_LIST_DEFAULT_PARAMS } from "./contactsParams";
import { isOfflineFeatureEnabled } from "./featureFlag";
import { isOnline } from "./isOnline";
import {
  DEFAULT_STATUS,
  PrefetchStatus,
  isStale,
  readPrefetchStatus,
  writePrefetchStatus,
} from "./prefetchStatus";
import { ROUTE_DETAIL_PARAMS, SAVED_ROUTES_LIST_PARAMS } from "./routeParams";
import { estimateStorageHealth } from "./storageHealth";

/** Enough to hold every route of a large workspace in one response. */
const ROUTES_PER_PAGE = 500;

/**
 * Full contact details are the largest thing downloaded, so they come in pages
 * rather than one response a phone has to hold in memory whole. 200 households is
 * a few hundred KB per page; the cap is a runaway guard, not a real limit — it
 * covers 20,000 households, well past the largest workspace.
 */
const HOUSEHOLD_DETAILS_PER_PAGE = 200;
const MAX_HOUSEHOLD_DETAIL_PAGES = 100;

/**
 * Every household in the workspace, with the relations a contact card needs.
 *
 * The card, the edit form and the report wizard all read one shared `household`
 * field, filled by `households/{id}` — a per-household URL nothing warms, so
 * offline it is a cache miss for every contact whose card was never opened, and
 * the card spins forever on someone visible in the list right beside it. Fetching
 * those URLs individually would be thousands of requests; this is the same data in
 * a handful, held in Redux (and so in IndexedDB) rather than the SW cache.
 */
const fetchHouseholdDetails = async (): Promise<any[]> => {
  const all: any[] = [];
  let page = 1;
  let lastPage = 1;

  do {
    if (!isOnline()) throw new AbortPrefetch("offline");
    const response: any = await householdsAPI.getHouseholdDetails({
      page,
      perPage: HOUSEHOLD_DETAILS_PER_PAGE,
    });
    all.push(...(response?.data?.data ?? []));
    lastPage = Number(response?.data?.meta?.last_page ?? page);
    page += 1;
  } while (page <= lastPage && page <= MAX_HOUSEHOLD_DETAIL_PAGES);

  return all;
};

const statusOf = (error: any): number | undefined => error?.response?.status;

/** Raised internally to unwind the run when continuing would be pointless. */
class AbortPrefetch extends Error {
  constructor(public readonly reason: "auth" | "offline") {
    super(`prefetch aborted: ${reason}`);
  }
}

/**
 * The reference lists the visit-report wizard loads every time it opens. Global
 * URLs, so one fetch each covers every household — and without `getStatuses` in
 * particular the report cannot be submitted at all, since a status is required and
 * the dropdown would be empty on a phone that has never opened a report online.
 */
const REPORT_REFERENCE_LISTS = [
  () => visitReportsAPI.getStatuses(),
  () => visitReportsAPI.getReactions(),
  () => visitReportsAPI.getAddressError(),
  () => personAPI.getSalutations(),
  () => addressAPI.getTypes(),
];

/** A 401 anywhere means the session is dead; a missing response means the network is. */
const rethrowFatal = (error: any): void => {
  if (statusOf(error) === 401) throw new AbortPrefetch("auth");
  if (statusOf(error) === undefined) throw new AbortPrefetch("offline");
};

/**
 * Warms the per-household URLs the visit-report wizard requests, for every stop on
 * the volunteer's routes.
 *
 * This is what makes "log a visit report in a dead zone" actually work. The wizard
 * asks for `households/{id}` and `households/{id}/get-next-scheduled-visit`, both
 * addressed per household and neither reachable from anything else the prefetch
 * fetches — so before this, opening a report for a household not already visited
 * online found no visit to file against, and the report either failed to sync or
 * (before the guard in useVisitReportForm) was filed against whichever household
 * had been opened last.
 *
 * Scoped to route stops, not the whole contact book: a workspace can hold thousands
 * of households and two requests each is not a download anyone would wait through.
 * The stops on your routes are the ones you report on in the field.
 *
 * Returns the number of households whose data could not be fetched.
 */
const warmReportData = async (householdIds: number[]): Promise<number> => {
  let failed = 0;

  for (const list of REPORT_REFERENCE_LISTS) {
    if (!isOnline()) throw new AbortPrefetch("offline");
    try {
      await list();
    } catch (error) {
      rethrowFatal(error);
      failed += 1;
    }
  }

  for (const id of householdIds) {
    if (!isOnline()) throw new AbortPrefetch("offline");
    try {
      // Order matters only for the 401 case: the first request of the pair is the
      // one that discovers a dead session, and the second is then never made.
      await householdsAPI.getHouseholdsById(id);
      await visitReportsAPI.getNextHouseholdVisit(id);
    } catch (error) {
      rethrowFatal(error);
      // One household's report being unavailable must not cost the other stops
      // theirs — record it and keep going, exactly as a failed route does.
      failed += 1;
    }
  }

  return failed;
};

let running = false;
let rerunRequested = false;

/** Test-only: release the single-flight latch between cases. */
export const __resetPrefetchLatchForTests = (): void => {
  running = false;
  rerunRequested = false;
};

/**
 * One full pass. Never call this directly — `runPrefetch` owns the latch that
 * stops two passes overlapping.
 *
 * Routes go first and one at a time: a run interrupted halfway should still leave
 * them able to work their route, which is the case that actually strands people.
 * The contact book is the fallback for "who else is nearby", so it comes second.
 */
const runPrefetchPass = async (dispatch: OfflineDispatch): Promise<PrefetchStatus> => {
  let status: PrefetchStatus = { ...DEFAULT_STATUS, state: "running" };
  const publish = () => dispatch(offlineActions.setPrefetchAC(status));
  publish();

  try {
    const health = await estimateStorageHealth();
    status = { ...status, storageLimited: health === "restricted" };

    const listResponse: any = await routesAPI.getRoutes({ perPage: ROUTES_PER_PAGE, page: 1 } as any);
    const routes: Array<{ id: number }> = listResponse?.data?.data ?? [];
    status = { ...status, routesTotal: routes.length };
    publish();

    // The bulk call above is for the engine's own use — it enumerates every route
    // id to walk. It is NOT the request SavedRoutesPage issues, which carries the
    // page's own params and so produces a different query string and a different
    // Workbox cache key. Without this second call the routes are all cached and
    // none of them reachable: the list screen misses offline and comes up empty.
    //
    // Its own try/catch, because it sits BEFORE the route loop and must never be
    // able to stop it: bare, a single 500 or gateway blip on this one request
    // unwound to the catch below with routesDone still 0, so nothing at all got
    // cached — the exact inverse of the routes-first guarantee above. Warming this
    // URL matters, but not more than the route details it links to.
    try {
      await routesAPI.getRoutes({ ...SAVED_ROUTES_LIST_PARAMS });
    } catch (error) {
      // One exception: a 401 means the session is dead, so every request after
      // this would fail identically. Same reading the route loop gives it.
      if (statusOf(error) === 401) throw new AbortPrefetch("auth");
    }

    // Every household with a stop on one of these routes, deduplicated: the same
    // household can appear on two routes, and its report data is the same either way.
    const routeHouseholdIds = new Set<number>();

    for (const route of routes) {
      if (!isOnline()) throw new AbortPrefetch("offline");
      try {
        const detail: any = await routesAPI.getRoute({ id: route.id, ...ROUTE_DETAIL_PARAMS } as any);
        // PointResource carries `household_id` directly, so this does not depend on
        // the nested household relation being loaded.
        for (const point of detail?.data?.data?.points ?? []) {
          if (point?.household_id) routeHouseholdIds.add(Number(point.household_id));
        }
        status = { ...status, routesDone: status.routesDone + 1 };
      } catch (error) {
        if (statusOf(error) === 401) throw new AbortPrefetch("auth");
        if (statusOf(error) === undefined) throw new AbortPrefetch("offline");
        status = { ...status, routesFailed: status.routesFailed + 1 };
      }
      publish();
    }

    // Skipping the contact book under a restricted quota is deliberate: routes are
    // what strand a volunteer, and writing 4 MB into a quota that cannot hold it
    // would evict the routes we just fetched.
    if (!status.storageLimited) {
      // Before the contact book: being able to file a report on a stop you already
      // have is worth more in a dead zone than being able to look up a household
      // that isn't on your route. Skipped under a restricted quota for the same
      // reason the contact book is — the routes come first and must not be evicted.
      const reportsFailed = await warmReportData(Array.from(routeHouseholdIds));
      status = { ...status, reportsFailed };
      publish();

      if (!isOnline()) throw new AbortPrefetch("offline");
      // No perPage: the backend returns the full unpaginated collection without it.
      const households: any = await householdsAPI.getHouseholds({});
      dispatch(householdsActions.getHouseholdsAC(households.data));
      // Same response, written to a SEPARATE slice field so ContactsPage can browse
      // it offline without touching `households` (the server-paginated slice every
      // other screen reads) — see the `offlineSnapshot` field comment in
      // householdsReducer.ts for why the two must never be merged.
      dispatch(householdsActions.setHouseholdsOfflineSnapshotAC(households.data.data));

      // The bulk call above is for the Redux slice (persisted whole, by redux-
      // persist) — it is NOT the request ContactsPage itself issues, which carries
      // that page's own default pagination/sort/filter params and so produces a
      // different query string and a different Workbox cache key. Without this
      // second call the contact book is all in Redux but the list SCREEN's own
      // request misses offline, same failure mode as the route-list warm-up above.
      //
      // Built from the same action creator ContactsPage dispatches, applied to the
      // shared default-params constant, so the two payloads can't drift apart —
      // and its own try/catch for the same reason as the route-list warm-up: this
      // is a nice-to-have on top of the unpaginated fetch just above, which must
      // stand even if this one call fails.
      try {
        const warmUpPayload = householdsTriggerActions.getHouseholdsTriggerAC(CONTACTS_LIST_DEFAULT_PARAMS).payload;
        await householdsAPI.getHouseholds(warmUpPayload);
      } catch (error) {
        // Same one exception as the route-list warm-up: a dead session means
        // every request after this would fail identically anyway.
        if (statusOf(error) === 401) throw new AbortPrefetch("auth");
      }

      const categories: any = await categoriesAPI.getCategories();
      dispatch(categoriesActions.getCategoriesAC(categories.data.data));

      // Last, because it is the largest: the list above is what ContactsPage renders,
      // and this is what opening any one of those rows needs. Dispatched once, whole,
      // so a run interrupted mid-paging leaves the previous details untouched rather
      // than replacing them with a partial set.
      dispatch(householdsActions.setHouseholdDetailsOfflineSnapshotAC(await fetchHouseholdDetails()));

      status = { ...status, contactsDone: true };
    }

    const complete =
      status.routesFailed === 0 && status.contactsDone && status.reportsFailed === 0;
    status = {
      ...status,
      state: complete ? "ready" : "partial",
      // Only a complete run sets the timestamp. A partial run stays stale so the
      // 12-hour rule keeps retrying instead of waiting out the day.
      lastCompletedAt: complete ? Date.now() : null,
    };
  } catch (error) {
    if (error instanceof AbortPrefetch) {
      status = { ...status, state: error.reason === "auth" ? "failed" : "partial" };
    } else if (statusOf(error) === 401) {
      status = { ...status, state: "failed" };
    } else {
      status = { ...status, state: status.routesDone > 0 ? "partial" : "failed" };
    }
  } finally {
    writePrefetchStatus(status);
    publish();
  }

  return status;
};

/**
 * Downloads everything a volunteer needs in a dead zone, one pass at a time.
 *
 * A trigger that arrives mid-run cannot just be dropped. The one that matters is
 * the post-sync refresh: its whole job is to correct the visited flags for stops
 * the volunteer just synced, and at boot it lands while the boot pass — started a
 * tick earlier and holding the latch — is still fetching. Dropping it left the
 * cached routes showing those stops as unvisited, and the boot pass then stamped
 * `ready` with a fresh timestamp, so the 12-hour rule suppressed any retry for the
 * rest of the day. So the request is remembered and honoured with one more pass.
 */
export const runPrefetch = async (dispatch: OfflineDispatch): Promise<PrefetchStatus> => {
  // Copy, not the shared singleton: a caller mutating the returned object must
  // never corrupt DEFAULT_STATUS for the rest of the session.
  if (!isOfflineFeatureEnabled()) return { ...DEFAULT_STATUS };
  if (!isOnline()) return readPrefetchStatus();
  // A second trigger mid-run must not start a parallel pass: it would double every
  // request and race the two status writes against each other. Queue it instead.
  if (running) {
    rerunRequested = true;
    return readPrefetchStatus();
  }
  running = true;

  try {
    const first = await runPrefetchPass(dispatch);

    // Exactly one extra pass, never a chain: the flag is cleared unconditionally in
    // the `finally` below, so a trigger arriving during the extra pass is dropped
    // rather than scheduling a third. Two passes already covers the real case (a
    // flush finishing under a boot run); anything beyond that is a trigger storm
    // whose data would be redundant, and recursing on it would let a caller in a
    // loop keep the engine fetching forever.
    if (rerunRequested && isOnline()) {
      rerunRequested = false;
      return await runPrefetchPass(dispatch);
    }

    return first;
  } finally {
    running = false;
    rerunRequested = false;
  }
};

/** The background top-up: runs only when the snapshot is missing, old or incomplete. */
export const runPrefetchIfStale = async (dispatch: OfflineDispatch): Promise<void> => {
  if (!isOfflineFeatureEnabled() || !isOnline()) return;
  if (!isStale(readPrefetchStatus(), Date.now())) return;
  await runPrefetch(dispatch);
};
